import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as d3 from 'd3';
import {
  Code,
  Play,
  Loader2,
  Download,
  AlertTriangle,
  CheckCircle2,
  Sparkles,
  Database,
  FileCode,
  TrendingUp,
  Filter,
  Zap,
  GitMerge,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SPDDSPAUPredictor = ({ project }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [useMockData, setUseMockData] = useState(true);
  const [showDataSourceModal, setShowDataSourceModal] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterModule, setFilterModule] = useState('all');
  
  const heatmapRef = useRef(null);

  useEffect(() => {
    fetchLatest();
  }, [project.id]);

  useEffect(() => {
    if (analysis && activeTab === 'overview') {
      renderModuleHeatmap();
    }
  }, [analysis, activeTab]);

  const fetchLatest = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/spdd-spau/latest`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.log('No SPDD/SPAU analysis yet');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/projects/${project.id}/spdd-spau/analyze`,
        { use_mock_data: useMockData },
        
      );
      setAnalysis(response.data);
      setShowDataSourceModal(false);
    } catch (error) {
      console.error('Error:', error);
      alert('Error running SPDD/SPAU analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const renderModuleHeatmap = () => {
    if (!heatmapRef.current || !analysis) return;

    const svg = d3.select(heatmapRef.current);
    svg.selectAll('*').remove();

    const data = analysis.sum_preview.conflict_hotspots;
    if (!data || data.length === 0) return;

    const width = 700;
    const height = 300;
    const margin = { top: 30, right: 30, bottom: 60, left: 60 };

    svg.attr('width', width).attr('height', height);

    const x = d3.scaleBand()
      .domain(data.map(d => d.module))
      .range([margin.left, width - margin.right])
      .padding(0.2);

    const y = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.count) || 10])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const colorScale = d3.scaleSequential()
      .domain([0, d3.max(data, d => d.count) || 10])
      .interpolator(d3.interpolateReds);

    // Bars
    svg.selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', d => x(d.module))
      .attr('y', d => y(d.count))
      .attr('width', x.bandwidth())
      .attr('height', d => y(0) - y(d.count))
      .attr('fill', d => colorScale(d.count))
      .attr('rx', 4);

    // Values
    svg.selectAll('text.value')
      .data(data)
      .join('text')
      .attr('class', 'value')
      .attr('x', d => x(d.module) + x.bandwidth() / 2)
      .attr('y', d => y(d.count) - 5)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.count);

    // X axis
    svg.append('g')
      .attr('transform', `translate(0,${height - margin.bottom})`)
      .call(d3.axisBottom(x));

    // Y axis
    svg.append('g')
      .attr('transform', `translate(${margin.left},0)`)
      .call(d3.axisLeft(y));

    // Title
    svg.append('text')
      .attr('x', width / 2)
      .attr('y', 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '14px')
      .style('font-weight', 'bold')
      .text('Conflict Hotspots by Module');
  };

  const exportBacklog = () => {
    if (!analysis) return;

    const csv = [
      ['Object', 'Type', 'Module', 'SPDD/SPAU', 'Strategy', 'Effort', 'Risk', 'Status'].join(','),
      ...analysis.recommendations.map(r => [
        r.object_name,
        r.object_type,
        r.module,
        r.spdd_spau_classification,
        r.recommended_strategy,
        r.effort_band,
        r.conflict_risk,
        r.delta_type
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AURA_SPDD_SPAU_Backlog_${project.name.replace(/\s/g, '_')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStrategyColor = (strategy) => {
    if (strategy === 'RESET') return 'bg-green-600 text-white';
    if (strategy === 'ADJUST') return 'bg-yellow-600 text-white';
    if (strategy === 'RETAIN_MOD') return 'bg-orange-600 text-white';
    return 'bg-purple-600 text-white';
  };

  const getRiskColor = (risk) => {
    if (risk === 'high') return 'bg-red-600 text-white';
    if (risk === 'medium') return 'bg-yellow-500 text-white';
    return 'bg-green-500 text-white';
  };

  const filteredRecommendations = analysis?.recommendations.filter(r => {
    if (filterType !== 'all' && r.spdd_spau_classification !== filterType) return false;
    if (filterModule !== 'all' && r.module !== filterModule) return false;
    return true;
  }) || [];

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-12 h-12 text-purple-600 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 border border-gray-100">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">SPDD/SPAU Predictor & Remediation Planner</h2>
            <p className="text-gray-600">Predict upgrade conflicts before SUM/DMO execution</p>
            
            {/* SAP System Indicator */}
            {analysis && analysis.sap_system_analyzed && analysis.sap_system_analyzed.environment_name && (
              <div className="mt-3 flex items-center space-x-3 text-sm">
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-pink-50 border border-pink-200 rounded-lg">
                  <Shield className="w-4 h-4 text-pink-600" />
                  <span className="text-gray-700">Analyzing:</span>
                  <span className="font-bold text-pink-600">{analysis.sap_system_analyzed.environment_name}</span>
                  <span className="text-gray-500">({analysis.sap_system_analyzed.host})</span>
                </div>
                {analysis.data_source === 'REAL_SAP_SYSTEM' && (
                  <div className="px-3 py-1.5 bg-green-100 border border-green-300 rounded-lg">
                    <span className="text-green-800 font-semibold flex items-center space-x-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>📊 Real Data</span>
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {analysis && (
              <button 
                onClick={exportBacklog}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export CSV</span>
              </button>
            )}
            <button 
              onClick={() => setShowDataSourceModal(true)}
              disabled={analyzing}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl hover:shadow-lg flex items-center space-x-2 disabled:opacity-50"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  <span>Run Analysis</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Data Source Modal */}
      {showDataSourceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold mb-6">Select Data Source</h3>
            
            <div className="space-y-4">
              <div 
                onClick={() => setUseMockData(true)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  useMockData ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Database className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Mock SAP Objects</p>
                    <p className="text-sm text-gray-600">Use sample modified objects (BKPF, VBAK, etc.)</p>
                  </div>
                </div>
              </div>

              <div 
                onClick={() => setUseMockData(false)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  !useMockData ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Zap className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Real SAP System</p>
                    <p className="text-sm text-gray-600">Fetch from configured SAP connection</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button 
                onClick={runAnalysis}
                disabled={analyzing}
                className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 disabled:opacity-50"
              >
                {analyzing ? 'Analyzing...' : 'Run Analysis'}
              </button>
              <button 
                onClick={() => setShowDataSourceModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!analysis && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <GitMerge className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No SPDD/SPAU Analysis</h3>
          <p className="text-gray-600 mb-6">Predict upgrade conflicts before running SUM/DMO</p>
          <button 
            onClick={() => setShowDataSourceModal(true)}
            className="bg-purple-600 text-white px-8 py-3 rounded-xl hover:bg-purple-700 inline-flex items-center space-x-2"
          >
            <Play className="w-5 h-5" />
            <span>Run Prediction</span>
          </button>
        </div>
      )}

      {/* Main Content */}
      {analysis && (
        <>
          {/* Summary Cards */}
          <div className="grid md:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <Code className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.total_modified_objects}</div>
              <div className="text-purple-100 text-sm">Modified Objects</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
              <Database className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.spdd_count}</div>
              <div className="text-blue-100 text-sm">SPDD (Dictionary)</div>
            </div>
            <div className="bg-gradient-to-br from-pink-500 to-pink-600 rounded-xl p-6 text-white shadow-lg">
              <FileCode className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.spau_count}</div>
              <div className="text-pink-100 text-sm">SPAU (Repository)</div>
            </div>
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
              <AlertTriangle className="w-10 h-10 mb-3 opacity-80" />
              <div className="text-3xl font-bold mb-2">{analysis.summary.critical_conflicts}</div>
              <div className="text-orange-100 text-sm">Critical Conflicts</div>
            </div>
          </div>

          {/* Data Source Badge */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Data Source:</span> {analysis.data_source}
            </p>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">
            <div className="flex border-b border-gray-200 overflow-x-auto">
              {[
                { id: 'overview', label: 'Overview', icon: TrendingUp },
                { id: 'objects', label: 'Modified Objects', icon: Code },
                { id: 'recommendations', label: 'Recommendations', icon: CheckCircle2 },
                { id: 'groups', label: 'Remediation Groups', icon: GitMerge },
                { id: 'sum-preview', label: 'SUM Preview', icon: Sparkles }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-6 py-3 font-medium transition-all flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Conflict Heatmap */}
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Conflict Hotspots by Module</h4>
                    <div className="flex justify-center">
                      <svg ref={heatmapRef}></svg>
                    </div>
                  </div>

                  {/* Summary Stats */}
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Effort Estimation</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Hours:</span>
                          <span className="font-bold text-gray-900">{analysis.summary.total_effort_hours}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Days:</span>
                          <span className="font-bold text-gray-900">{analysis.summary.total_effort_days}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Work Groups:</span>
                          <span className="font-bold text-gray-900">{analysis.summary.group_count}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Conflict Severity</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Critical:</span>
                          <span className="font-bold text-red-600">{analysis.summary.critical_conflicts}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Complicated:</span>
                          <span className="font-bold text-orange-600">{analysis.summary.complicated_conflicts}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Simple:</span>
                          <span className="font-bold text-green-600">
                            {analysis.summary.total_modified_objects - analysis.summary.critical_conflicts - analysis.summary.complicated_conflicts}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <h4 className="font-bold text-gray-900 mb-4">Strategy Distribution</h4>
                      <div className="space-y-2">
                        {['RESET', 'ADJUST', 'REFACTOR_TO_EXTENSION'].map(strategy => {
                          const count = analysis.recommendations.filter(r => r.recommended_strategy === strategy).length;
                          return (
                            <div key={strategy} className="flex justify-between text-sm">
                              <span className="text-gray-600">{strategy.replace(/_/g, ' ')}:</span>
                              <span className="font-bold">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Modified Objects Tab */}
              {activeTab === 'objects' && (
                <div className="space-y-4">
                  {analysis.delta_analysis.map((obj) => (
                    <div key={obj.object_name} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              obj.spdd_spau_type === 'SPDD' ? 'bg-blue-600 text-white' : 'bg-pink-600 text-white'
                            }`}>
                              {obj.spdd_spau_type}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{obj.object_name}</h4>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs mb-3">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {obj.object_type}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {obj.module}
                            </span>
                            <span className={`px-2 py-1 rounded ${getRiskColor(obj.conflict_risk)}`}>
                              {obj.conflict_risk.toUpperCase()} RISK
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className={`text-2xl font-bold ${
                            obj.likely_spdd_spau_status === 'critical' ? 'text-red-600' :
                            obj.likely_spdd_spau_status === 'complicated' ? 'text-orange-600' :
                            'text-green-600'
                          }`}>
                            {obj.likely_spdd_spau_status.toUpperCase()}
                          </div>
                          <div className="text-xs text-gray-500">Status</div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-700"><span className="font-semibold">Delta Type:</span> {obj.delta_type}</p>
                        <p className="text-sm text-gray-700 mt-1"><span className="font-semibold">Changes:</span> {obj.changes_summary}</p>
                        {obj.complexity_metrics && (
                          <div className="mt-2 text-xs text-gray-600">
                            {Object.entries(obj.complexity_metrics).map(([key, value]) => (
                              <span key={key} className="mr-3">
                                {key.replace(/_/g, ' ')}: {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recommendations Tab */}
              {activeTab === 'recommendations' && (
                <div className="space-y-4">
                  {/* Filters */}
                  <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select 
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Types</option>
                      <option value="SPDD">SPDD Only</option>
                      <option value="SPAU">SPAU Only</option>
                    </select>
                    <select 
                      value={filterModule}
                      onChange={(e) => setFilterModule(e.target.value)}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="all">All Modules</option>
                      {['FI', 'CO', 'MM', 'SD', 'PP', 'HCM', 'Cross'].map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                    <span className="text-sm text-gray-600">{filteredRecommendations.length} items</span>
                  </div>

                  {/* Recommendations List */}
                  {filteredRecommendations.map((rec) => (
                    <div key={rec.object_name} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStrategyColor(rec.recommended_strategy)}`}>
                              {rec.recommended_strategy.replace(/_/g, ' ')}
                            </span>
                            <h4 className="text-lg font-bold text-gray-900">{rec.object_name}</h4>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded">
                              {rec.object_type}
                            </span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                              {rec.module}
                            </span>
                            <span className={`px-2 py-1 rounded ${
                              rec.spdd_spau_classification === 'SPDD' ? 'bg-blue-600 text-white' : 'bg-pink-600 text-white'
                            }`}>
                              {rec.spdd_spau_classification}
                            </span>
                          </div>
                        </div>
                        <div className="text-right ml-4">
                          <div className="text-2xl font-bold text-purple-600">{rec.effort_band}</div>
                          <div className="text-xs text-gray-500">Effort Band</div>
                          <div className="text-xs text-gray-600 mt-1">
                            Probability: {(rec.conflict_probability * 100).toFixed(0)}%
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Rationale:</p>
                        <p className="text-sm text-gray-700">{rec.rationale}</p>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mt-4">
                        <div className="text-sm">
                          <span className="text-gray-600">Delta Type:</span>
                          <span className="font-semibold ml-2 capitalize">{rec.delta_type}</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-600">Conflict Risk:</span>
                          <span className={`font-semibold ml-2 capitalize ${
                            rec.conflict_risk === 'high' ? 'text-red-600' :
                            rec.conflict_risk === 'medium' ? 'text-orange-600' :
                            'text-green-600'
                          }`}>
                            {rec.conflict_risk}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Remediation Groups Tab */}
              {activeTab === 'groups' && (
                <div className="space-y-4">
                  {analysis.remediation_groups.map((group) => (
                    <div key={group.group_id} className="bg-white border-2 border-purple-200 rounded-xl p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">{group.group_id}</h4>
                          <p className="text-sm text-gray-600">Module: {group.module} • Effort Band: {group.effort_band}</p>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-600">{group.total_effort_hours}h</div>
                          <div className="text-xs text-gray-500">Total Effort</div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <div className="text-xl font-bold text-blue-600">{group.spdd_count}</div>
                          <div className="text-xs text-gray-600">SPDD Objects</div>
                        </div>
                        <div className="bg-pink-50 rounded-lg p-3 text-center">
                          <div className="text-xl font-bold text-pink-600">{group.spau_count}</div>
                          <div className="text-xs text-gray-600">SPAU Objects</div>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-3 text-center">
                          <div className="text-xl font-bold text-purple-600">#{group.recommended_sequence}</div>
                          <div className="text-xs text-gray-600">Sequence</div>
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-900 mb-2">Objects ({group.objects.length}):</p>
                        <div className="flex flex-wrap gap-2">
                          {group.objects.map((obj, idx) => (
                            <span key={idx} className="px-2 py-1 bg-white border border-gray-300 text-gray-700 text-xs rounded">
                              {obj}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* SUM Preview Tab */}
              {activeTab === 'sum-preview' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                    <h3 className="text-xl font-bold mb-4">Expected SUM/DMO Conflicts</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">SPDD Objects</p>
                        <p className="text-4xl font-bold text-blue-600">{analysis.sum_preview.expected_spdd_objects}</p>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">SPAU Objects</p>
                        <p className="text-4xl font-bold text-pink-600">{analysis.sum_preview.expected_spau_objects}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Top 10 Risk Objects</h4>
                    <div className="space-y-2">
                      {analysis.sum_preview.top_risk_objects.map((obj, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{obj.object}</p>
                              <p className="text-xs text-gray-600">{obj.type}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                              obj.status === 'critical' ? 'bg-red-600 text-white' :
                              obj.status === 'complicated' ? 'bg-orange-500 text-white' :
                              'bg-green-500 text-white'
                            }`}>
                              {obj.status.toUpperCase()}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${getRiskColor(obj.risk)}`}>
                              {obj.risk.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-4">Module Breakdown</h4>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="border border-gray-300 px-4 py-2 text-left font-bold">Module</th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-bold">SPDD</th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-bold">SPAU</th>
                            <th className="border border-gray-300 px-4 py-2 text-center font-bold">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(analysis.sum_preview.module_breakdown).map(([module, counts]) => (
                            <tr key={module}>
                              <td className="border border-gray-300 px-4 py-2 font-semibold">{module}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">{counts.spdd}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center">{counts.spau}</td>
                              <td className="border border-gray-300 px-4 py-2 text-center font-bold">{counts.total}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SPDDSPAUPredictor;
